// backlog-review.ts — Logique PURE de l'écran de revue (filtres, plafond, tri).
//
// Extraite de `page-backlog-review.ts` pour être testable sans DOM, comme le
// reste de `domain/`. Trois règles de l'écran vivent ici, et ce sont les trois
// qu'une régression casserait en silence :
//
// 1. **Le plafond porte sur les recommandations NON TRAITÉES, jamais sur la
//    détection.** Tout ce qui a été trouvé est en base ; à mesure que la pile
//    « à traiter » se vide, les suivantes entrent d'elles-mêmes, sans itérer.
//    Si le plafond s'appliquait à la détection, le message « traitez ces 25,
//    les suivantes remonteront » serait un mensonge.
// 2. **L'ordre est stable** : famille, puis confiance décroissante, puis
//    identifiant. La liste ne se réordonne jamais après une action.
// 3. **Les filtres se cumulent** : pile, famille et drapeau sont trois axes
//    indépendants.

export interface RecoLike {
  id: number;
  family: string;
  confidence: string;
  pile: string;
  to_arbitrate: boolean;
}

/** Les deux ordres proposés à l'utilisateur.
 *
 * `reco` reste le défaut : c'est l'ordre de lecture d'une revue — on traite par
 * famille, et au sein d'une famille les propositions les mieux étayées d'abord.
 *
 * Un tri par PRIORITÉ a existé, retiré le 2026-08-26 avec la notion de priorité
 * elle-même : trier sur un critère que l'écran n'affiche nulle part obligeait à
 * deviner pourquoi une carte passait devant une autre.
 */
export type SortKey = 'reco' | 'id';

export interface ReviewFilters {
  pile?: string;
  family?: string;
  onlyFlagged?: boolean;
}

const CONFIDENCE_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 };

/** Filtre cumulatif : pile ∧ famille ∧ drapeau. */
export function filterRecommendations<T extends RecoLike>(rows: T[], f: ReviewFilters): T[] {
  return rows.filter(
    (r) =>
      (!f.pile || r.pile === f.pile) &&
      (!f.family || r.family === f.family) &&
      (!f.onlyFlagged || r.to_arbitrate),
  );
}

/** Tri stable, indépendant de l'ordre d'arrivée du serveur.
 *
 * Quel que soit l'ordre choisi, l'identifiant tranche en dernier ressort : sans
 * lui deux lectures de la même revue pourraient différer, et la liste se
 * réordonnerait sous les doigts après chaque arbitrage.
 */
export function sortRecommendations<T extends RecoLike>(rows: T[], key: SortKey = 'reco'): T[] {
  return [...rows].sort((a, b) => {
    if (key === 'id') return a.id - b.id;
    if (a.family !== b.family) return a.family < b.family ? -1 : 1;
    const ca = CONFIDENCE_ORDER[a.confidence] ?? 3;
    const cb = CONFIDENCE_ORDER[b.confidence] ?? 3;
    if (ca !== cb) return ca - cb;
    return a.id - b.id;
  });
}

/** Compte par pile, toutes les piles présentes même à zéro. */
export function pileCounts(rows: RecoLike[], piles: readonly string[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const p of piles) out[p] = 0;
  for (const r of rows) out[r.pile] = (out[r.pile] ?? 0) + 1;
  return out;
}

export interface CappedView<T> {
  shown: T[];
  total: number;
  capped: boolean;
}

/** Applique le plafond d'AFFICHAGE — et seulement à la pile « à traiter ».
 *
 * Les autres piles sont des archives de décisions : les tronquer cacherait ce
 * que le PMO a déjà fait, ce que rien ne justifie.
 */
export function capForDisplay<T extends RecoLike>(
  rows: T[],
  cap: number,
  opts: { pile: string; showAll: boolean },
): CappedView<T> {
  const total = rows.length;
  if (opts.showAll || opts.pile !== 'todo' || total <= cap) {
    return { shown: rows, total, capped: false };
  }
  return { shown: rows.slice(0, cap), total, capped: true };
}
